// Formatting + zone-color helpers for WorkoutDetail page.
// Kept separate from tss.ts (plan/TSS formatting) so WorkoutDetail can evolve
// without touching shared helpers.

import type { HrZoneBin } from "@/types";
import { THEME, ZONE_COLOR } from "@/lib/theme";

export function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return "—";
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function formatPace(secondsPerKm: number | null | undefined): string {
  if (!secondsPerKm || secondsPerKm <= 0 || !isFinite(secondsPerKm)) return "—";
  const m = Math.floor(secondsPerKm / 60);
  const s = Math.round(secondsPerKm % 60);
  const ss = String(s).padStart(2, "0");
  return `${m}:${ss}/км`;
}

export function formatDistance(metres: number | null | undefined): string {
  if (metres === null || metres === undefined) return "—";
  if (metres < 1000) return `${Math.round(metres)} м`;
  const km = metres / 1000;
  return `${km.toFixed(km < 10 ? 2 : 1)} км`;
}

export function msToKmh(ms: number | null | undefined): number | null {
  if (ms === null || ms === undefined) return null;
  return ms * 3.6;
}

export function msToPaceSecPerKm(ms: number | null | undefined): number | null {
  if (!ms || ms <= 0) return null;
  return 1000 / ms;
}

export function formatElevation(metres: number | null | undefined): string {
  if (metres === null || metres === undefined) return "—";
  return `${Math.round(metres)} м`;
}

export function sportNameRu(sport: string): string {
  const map: Record<string, string> = {
    run: "Бег",
    bike: "Велосипед",
    ride: "Велосипед",
    swim: "Плавание",
    strength: "Силовая",
    walk: "Ходьба",
    yoga: "Йога",
    brick: "Брик",
    mobility: "Мобилити",
    rest: "Отдых",
  };
  return map[sport.toLowerCase()] || sport;
}

/**
 * Zone palette aligned with the cream/navy/amber brand — warm, not
 * neon. Z1 green (easy) → Z5+ deep coral (max effort).
 */
export function zoneColor(zone: number): string {
  return ZONE_COLOR[zone] || THEME.muted;
}

export function zoneLabel(zone: number): string {
  const labels: Record<number, string> = {
    1: "Recovery",
    2: "Endurance",
    3: "Tempo",
    4: "Threshold",
    5: "VO2max",
    6: "Anaerobic",
    7: "Neuromuscular",
  };
  return labels[zone] || `Z${zone}`;
}

export function computeZ5PlusTime(hrZones: Pick<HrZoneBin, "zone" | "time_s">[]): number {
  return hrZones.filter((z) => z.zone >= 5).reduce((sum, z) => sum + (z.time_s || 0), 0);
}

export function totalZoneTime(zones: Pick<HrZoneBin, "time_s">[]): number {
  return zones.reduce((sum, z) => sum + (z.time_s || 0), 0);
}

/**
 * Leaflet-style bounds [[south, west], [north, east]] from a latlng polyline.
 * Falls back to London if no points.
 */
export function computeBounds(latlng: number[][]): [[number, number], [number, number]] {
  if (!latlng || latlng.length === 0) {
    return [
      [51.5, -0.1],
      [51.5, -0.1],
    ];
  }
  const lats = latlng.map((p) => p[0]);
  const lngs = latlng.map((p) => p[1]);
  return [
    [Math.min(...lats), Math.min(...lngs)],
    [Math.max(...lats), Math.max(...lngs)],
  ];
}

export function feelLabel(feel: number | null): string | null {
  if (feel === null || feel === undefined) return null;
  const labels: Record<number, string> = {
    1: "тяжело",
    2: "непросто",
    3: "нормально",
    4: "легко",
    5: "отлично",
  };
  return labels[feel] || null;
}
